import {
  type ItemOption,
  POKEMON_ITEM_ID,
  POKEMON_ITEM_ID_NO_SET,
  DEFENCE_ITEM_ID_RUSTED_SWORD,
  DEFENCE_ITEM_ID_RUSTED_SHIELD,
  DEFENCE_ITEM_ID_DAIKONGOUDAMA,
  DEFENCE_ITEM_ID_DAISHIRATAMA,
  DEFENCE_ITEM_ID_DAIHAKKINDAMA,
  DEFENCE_ITEM_ID_AIIRO_NO_TAMA,
  DEFENCE_ITEM_ID_BENIIRO_NO_TAMA,
  DEFENCE_ITEM_ID_IDONOMEN,
  DEFENCE_ITEM_ID_KAMADONOMEN,
  DEFENCE_ITEM_ID_ISHIZUENOMEN,
  OFFENCE_ITEM_ID_AQUA_CASSETTE,
  OFFENCE_ITEM_ID_INAZUMA_CASSETTE,
  OFFENCE_ITEM_ID_BLAZE_CASSETTE,
  OFFENCE_ITEM_ID_FREEZE_CASSETTE,
  OFFENCE_ITEM_ID_FUTOIHONE,
  OFFENCE_ITEM_ID_KOKORONOSHIZUKU,
  OFFENCE_ITEM_ID_SHIRATAMA,
  OFFENCE_ITEM_ID_KONGOUDAMA,
  OFFENCE_ITEM_ID_HAKKINDAMA,
} from "../types/item";
import { getArceusTypeIdFromId, getPlateItemIdForArceusPokemon, getPlateItemIdForArceusType, isArceusFormId } from "../pokemon/arceus";
import type { PokemonV2 } from "../types/pokemon";
import { getMegaStoneItemIdForPokemon } from "./mega-stone";

// ザシアン/ザマゼンタ(けん/たてのおう)のフォームID
// 備考: 既存コードでは 10188, 10189 を直接参照していたため定数化する
export const POKEMON_ID_ZACIAN_CROWNED = 10188; // ザシアン(けんのおう)
export const POKEMON_ID_ZAMAZENTA_CROWNED = 10189; // ザマゼンタ(たてのおう)
export const POKEMON_ID_GENESECT = 649; // ゲノセクト
export const POKEMON_ID_KYOGRE_PRIMAL = 10077; // ゲンシカイオーガ
export const POKEMON_ID_GROUDON_PRIMAL = 10078; // ゲンシグラードン
export const POKEMON_ID_RAYQUAZA_MEGA = 10079; // レックウザ(メガレックウザ)
export const POKEMON_ID_SILVALLY = 773; // シルヴァディ(タイプ：ノーマル)

const GENESECT_ONLY_ITEM_IDS = new Set<POKEMON_ITEM_ID>([
  OFFENCE_ITEM_ID_AQUA_CASSETTE,
  OFFENCE_ITEM_ID_INAZUMA_CASSETTE,
  OFFENCE_ITEM_ID_BLAZE_CASSETTE,
  OFFENCE_ITEM_ID_FREEZE_CASSETTE,
]);

const SILVALLY_ONLY_ITEM_IDS = new Set<POKEMON_ITEM_ID>([
  910, // ファイヤーメモリ
  911, // ウオーターメモリ
  913, // エレクトロメモリ
  912, // グラスメモリ
  915, // アイスメモリ
  902, // ファイトメモリ
  904, // ポイズンメモリ
  905, // グラウンドメモリ
  903, // フライングメモリ
  914, // サイキックメモリ
  907, // バグメモリ
  906, // ロックメモリ
  908, // ゴーストメモリ
  916, // ドラゴンメモリ
  917, // ダークメモリ
  909, // スチールメモリ
  918, // フェアリーメモリ
]);

const THICK_CLUB_ONLY_POKEMON_IDS = new Set<number>([
  104, // カラカラ
  105, // ガラガラ
  10115, // ガラガラ(アローラのすがた)
]);

/**
 * 道具がゲノセクト専用（カセット系）かどうか
 */
export function isGenesectOnlyItemId(itemId: POKEMON_ITEM_ID | undefined): boolean {
  if (itemId == null) return false;
  return GENESECT_ONLY_ITEM_IDS.has(itemId);
}

/**
 * 道具がシルヴァディ専用（メモリ系）かどうか
 */
function isSilvallyOnlyItemId(itemId: POKEMON_ITEM_ID | undefined): boolean {
  if (itemId == null) return false;
  return SILVALLY_ONLY_ITEM_IDS.has(itemId);
}

/**
 * 指定ポケモンがその道具を所持可能か判定する
 * - 現状は「ゲノセクト専用カセット」を制限する目的で利用
 */
export function canPokemonHoldItem(pokemonId: number, itemId: POKEMON_ITEM_ID | undefined): boolean {
  if (itemId == null) return true;
  // 「ふといホネ」はカラカラ/ガラガラ系のみ
  if (itemId === OFFENCE_ITEM_ID_FUTOIHONE) {
    return THICK_CLUB_ONLY_POKEMON_IDS.has(pokemonId);
  }
  // 「こころのしずく」はラティアス/ラティオスのみ（メガは対象外）
  if (itemId === OFFENCE_ITEM_ID_KOKORONOSHIZUKU) {
    return pokemonId === 380 || pokemonId === 381;
  }
  // 「しらたま」はパルキアのみ
  if (itemId === OFFENCE_ITEM_ID_SHIRATAMA) {
    return pokemonId === 484;
  }
  // 「こんごうだま」はディアルガのみ
  if (itemId === OFFENCE_ITEM_ID_KONGOUDAMA) {
    return pokemonId === 483;
  }
  // 「はっきんだま」はギラティナのみ（アナザー/オリジン）
  if (itemId === OFFENCE_ITEM_ID_HAKKINDAMA) {
    return pokemonId === 487 || pokemonId === 10007;
  }
  // ザシアン(けんのおう)以外は「くちたけん」を持てない
  if (itemId === DEFENCE_ITEM_ID_RUSTED_SWORD) {
    return pokemonId === POKEMON_ID_ZACIAN_CROWNED;
  }
  // ザマゼンタ(たてのおう)以外は「くちたたて」を持てない
  if (itemId === DEFENCE_ITEM_ID_RUSTED_SHIELD) {
    return pokemonId === POKEMON_ID_ZAMAZENTA_CROWNED;
  }
  // ゲンシカイオーガ以外は「あいいろのたま」を持てない
  if (itemId === DEFENCE_ITEM_ID_AIIRO_NO_TAMA) {
    return pokemonId === POKEMON_ID_KYOGRE_PRIMAL;
  }
  // ゲンシグラードン以外は「べにいろのたま」を持てない
  if (itemId === DEFENCE_ITEM_ID_BENIIRO_NO_TAMA) {
    return pokemonId === POKEMON_ID_GROUDON_PRIMAL;
  }
  // シルヴァディ以外は「メモリ系アイテム」を持てない
  if (isSilvallyOnlyItemId(itemId)) {
    return pokemonId === POKEMON_ID_SILVALLY;
  }
  // ディアルガ(オリジンフォルム)以外は「だいこんごうだま」を持てない
  if (itemId === DEFENCE_ITEM_ID_DAIKONGOUDAMA) {
    return pokemonId === 10245;
  }
  // パルキア(オリジンフォルム)以外は「だいしらたま」を持てない
  if (itemId === DEFENCE_ITEM_ID_DAISHIRATAMA) {
    return pokemonId === 10246;
  }
  // ギラティナ(オリジンフォルム)以外は「だいはっきんだま」を持てない
  if (itemId === DEFENCE_ITEM_ID_DAIHAKKINDAMA) {
    return pokemonId === 10007;
  }
  if (!isGenesectOnlyItemId(itemId)) return true;
  return pokemonId === POKEMON_ID_GENESECT;
}

/**
 * UI用の道具候補をポケモンごとに絞り込む
 */
export function filterSelectableItemsForPokemon(pokemonId: number, items: ItemOption[]): ItemOption[] {
  return items.filter((item) => canPokemonHoldItem(pokemonId, item.id));
}

/**
 * ポケモンが持てない道具は「なし」に正規化する（保存データや持ち越し対策）
 */
export function normalizeItemIdForPokemon(
  pokemonId: number,
  itemId: POKEMON_ITEM_ID | undefined,
): POKEMON_ITEM_ID | undefined {
  if (itemId == null) return itemId;
  if (canPokemonHoldItem(pokemonId, itemId)) return itemId;
  return POKEMON_ITEM_ID_NO_SET;
}

/**
 * 指定ポケモンIDのフォーム固定アイテムを返す。
 * - 固定されない場合は null を返却。
 */
export function getFixedItemIdForPokemonId(pokemonId: number): POKEMON_ITEM_ID | null {
  // ザシアン/ザマゼンタ（けん/たてのおう）
  if (pokemonId === POKEMON_ID_ZACIAN_CROWNED) {
    return DEFENCE_ITEM_ID_RUSTED_SWORD;
  }
  if (pokemonId === POKEMON_ID_ZAMAZENTA_CROWNED) {
    return DEFENCE_ITEM_ID_RUSTED_SHIELD;
  }
  // ゲンシカイオーガ/ゲンシグラードン（オーブ固定）
  if (pokemonId === POKEMON_ID_KYOGRE_PRIMAL) {
    return DEFENCE_ITEM_ID_AIIRO_NO_TAMA;
  }
  if (pokemonId === POKEMON_ID_GROUDON_PRIMAL) {
    return DEFENCE_ITEM_ID_BENIIRO_NO_TAMA;
  }
  // オリジンフォルム系
  if (pokemonId === 10245) return DEFENCE_ITEM_ID_DAIKONGOUDAMA; // ディアルガ(オリジン)
  if (pokemonId === 10246) return DEFENCE_ITEM_ID_DAISHIRATAMA; // パルキア(オリジン)
  if (pokemonId === 10007) return DEFENCE_ITEM_ID_DAIHAKKINDAMA; // ギラティナ(オリジン)

  // オーガポン各種
  if (pokemonId === 10273) return DEFENCE_ITEM_ID_IDONOMEN; // いどのめん
  if (pokemonId === 10274) return DEFENCE_ITEM_ID_KAMADONOMEN; // かまどのめん
  if (pokemonId === 10275) return DEFENCE_ITEM_ID_ISHIZUENOMEN; // いしずえのめん

  if (isArceusFormId(pokemonId)) {
    const typeId = getArceusTypeIdFromId(pokemonId);
    if (typeId != null) return getPlateItemIdForArceusType(typeId);
  }
  return null;
}

/**
 * ポケモン情報からフォーム固定アイテムを返す。
 * - メガストーン（custom id含む）にも対応する。
 */
export function getFixedItemIdForPokemonEntity(pokemon: PokemonV2): POKEMON_ITEM_ID | null {
  const megaStone = getMegaStoneItemIdForPokemon(pokemon);
  if (megaStone != null) {
    return megaStone;
  }
  const arceusPlate = getPlateItemIdForArceusPokemon(pokemon);
  if (arceusPlate != null) {
    return arceusPlate;
  }
  return getFixedItemIdForPokemonId(pokemon.id);
}

/**
 * 指定ポケモンIDに対して自動で付与すべきアイテムがあれば返す。
 * - フォーム固定アイテムと同じルールを採用する。
 */
export function getAutoItemForPokemonId(pokemonId: number): POKEMON_ITEM_ID | null {
  // 将来的にここに他の自動付与ルールを追加していく
  return getFixedItemIdForPokemonId(pokemonId);
}

/**
 * ポケモン情報から自動で付与すべきアイテムがあれば返す。
 * - 既存のフォーム固定アイテム（ザシアン等）に加え、メガストーンの自動付与にも対応する。
 */
export function getAutoItemForPokemon(pokemon: PokemonV2): POKEMON_ITEM_ID | null {
  return getFixedItemIdForPokemonEntity(pokemon);
}

/**
 * 道具選択をロックすべき（特定フォーム専用アイテムが固定）かどうかを判定
 * UI側のSelectをdisabledにするために利用
 */
export function isItemLockedForPokemon(pokemonId: number): boolean {
  return getFixedItemIdForPokemonId(pokemonId) != null;
}

/**
 * ポケモン情報から道具選択をロックすべきか判定する。
 * - メガシンカ（メガストーン固定）を含む。
 */
export function isItemLockedForPokemonEntity(pokemon: PokemonV2): boolean {
  return getFixedItemIdForPokemonEntity(pokemon) != null;
}
